import React, { useEffect, useState } from "react";
import { Link, matchPath, useLocation } from "react-router-dom";
import {
  Home,
  Search,
  History,
  BarChart3,
  FileText,
  Users,
  Settings,
  Shield,
  Mail,
  X,
  ChevronDown,
  ChevronRight,
  Briefcase,
  Calendar,
  AlertCircle,
  CalendarClock,
  List,
  UserCheck,
  Target,
  User,
  XCircle,
  Files,
  Aperture,
  FilePlus2,
  Layers,
  BadgeCheck,
  AlarmClock,
  FolderKanban,
  Eye,
  UploadCloud,
  ClipboardCheck,
  Scale,
  BookOpen,
  RefreshCw,
  Star,
  Sparkles,
  FileSearch,
  Database,
  Sliders,
  Key,
  MessageSquare,
  ActivitySquare,
  LineChart,
  Menu,
  Sun,
  Moon,
  LogOut,
  HelpCircle,
  Bell,
  Zap,
  InfoIcon,
  Tag,
  Crown,
  LifeBuoy,
  ReceiptText,
  Palette,
  ScrollText,
  Globe,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { useTheme } from "../../contexts/ThemeContext";
import { ROUTES, USER_TYPES } from "../../constants/constants";
import config from "../../config/config";
import apiService from "../../services/api";
import { FaUserCog } from "react-icons/fa";
import { useTeamPermission } from "../../hooks/useTeamPermission";
import { FcDocument } from "react-icons/fc";
import { GrDocument } from "react-icons/gr";
import { PiArticleMediumFill } from "react-icons/pi";
import { BsWordpress } from "react-icons/bs";
import { BiSolidReport } from "react-icons/bi";
import { GiFocusedLightning } from "react-icons/gi";
import { LuImageDown } from "react-icons/lu";

/* ═══════════════════════════════════════════════════════════════════════════
   MODERN SIDEBAR COMPONENT - FIXED ALIGNMENT
   ═══════════════════════════════════════════════════════════════════════════ */
const Sidebar = ({ isOpen, onClose, brandColors, isMobile = false }) => {
  const location = useLocation();
  const { userType, getCompanyName, logout ,preferences } = useAuth();
  const { t } = useLanguage();
  const { isDark, toggleTheme } = useTheme();

const { hasTeamManagementAccess: hookTeamAccess, perms } = useTeamPermission();
  const companyName = getCompanyName();

  const [hasTMManagerAccess, setHasTMManagerAccess] = useState(false);
  const [hasWatchManagerAccess, setHasWatchManagerAccess] = useState(false);
  const [hasReportsAccess, setHasReportsAccess] = useState(false);
  const [hasApplyTrademarkAccess, setHasApplyTrademarkAccess] = useState(false);
  const [hasManageCustomerAccess, setHasManageCustomerAccess] = useState(false);
  const [canDraftLegal,  setCanDraftLegal]  = useState(false);
  const [canManageCases, setCanManageCases] = useState(false);
  const [canUseBrandGenerator, setCanUseBrandGenerator] = useState(false);
  const [licenseLoading, setLicenseLoading] = useState(true);
  const [watchPermissions, setWatchPermissions] = useState({
    Application_Level: false,
    Journal_Level: false,
    Examination_Level: false,
    Application_Status: false,
    Attorney_Status: false,
    Status_Change_Tracker: false,
  });
  const [hasPremiumFeaturesAccess, setHasPremiumFeaturesAccess] = useState(false);
  const [premiumFeaturesPermissions, setPremiumFeaturesPermissions] = useState({
    Astro_Mark: false,
    Proprietor_Status: false,
    Single_Click_Report: false,
    Focus_Word: false,
    Opponent_Strength_Permission: false,  // ✅ ADD THIS

  });
  const [reportsPermissions, setReportsPermissions] = useState({
    Search_Report: false,
  });

  const [hoveredItem, setHoveredItem] = useState(null);
  const [maxUsersAllowed, setMaxUsersAllowed] = useState(null);
  const [childCanManageUsers, setChildCanManageUsers] = useState(false);
  const [canManagePortfolio, setCanManagePortfolio] = useState(true);
const [canUseWhatsAppSupport, setCanUseWhatsAppSupport] = useState(false);
const [canUseWhatsAppSearch, setCanUseWhatsAppSearch] = useState(false);
const [canClientBillingPermission, setCanClientBillingPermission] = useState(false);
const [canTMAvailabilityPredictor, setCanTMAvailabilityPredictor] = useState(false);
const [canTaskManagement, setCanTaskManagement] = useState(false);
const [hasDesignSearchAccess, setHasDesignSearchAccess] = useState(false);
const [hasPatentSearchAccess, setHasPatentSearchAccess] = useState(false);
const [hasInternationalSearchAccess, setHasInternationalSearchAccess] = useState(false);
const [canClientEmailSetting, setCanClientEmailSetting] = useState(false);
const [canClientEmailTemplate, setCanClientEmailTemplate] = useState(false);
  useEffect(() => {
    let mounted = true;

    const loadClientLicense = async () => {
      try {
        setLicenseLoading(true);
        const res = await apiService.getClientLicense();
        // console.log("license-----------",res);
        if (!mounted) return;
        const allowed = res?.data?.activeLicense?.search_types_allowed || [];
        const allowedUser = res?.data?.activeLicense?.max_users ?? null;
        setMaxUsersAllowed(allowedUser);
        applyPermissions(allowed);
        setCanManagePortfolio(true);
      } catch (e) {
        // console.error("Client license fetch error:", e);
        resetPermissions();
      } finally {
        if (mounted) setLicenseLoading(false);
      }
    };

    const loadClientChildLicense = async () => {
      try {
        setLicenseLoading(true);
        const res = await apiService.getClientChildDetails();
        if (!mounted) return;

        const safeParsePerms = (val) => {
          if (!val) return [];
          if (Array.isArray(val)) return val;
          const s = String(val).trim();
          if (s.startsWith('[')) { try { return JSON.parse(s); } catch (_) {} }
          return s.replace(/[\[\]"]/g, '').split(',').map(v => v.trim()).filter(Boolean);
        };

        const userAllowed = safeParsePerms(res?.data?.user?.search_types_allowed);
        const parentLicenseAllowed = safeParsePerms(res?.data?.activeLicense?.search_types_allowed);

        const effectiveAllowed = parentLicenseAllowed.length > 0
          ? userAllowed.filter(p => parentLicenseAllowed.includes(p))
          : userAllowed;

        applyPermissions(effectiveAllowed);

        const userRolePerms = res?.data?.user?.role?.permissions;
        if (userRolePerms && !Array.isArray(userRolePerms) && userRolePerms.manage_users) {
          setChildCanManageUsers(true);
        }
        setCanManagePortfolio(true);
      } catch (e) {
        // console.error("Client child license fetch error:", e);
        resetPermissions();
      } finally {
        if (mounted) setLicenseLoading(false);
      }
    };

    const applyPermissions = (allowed = []) => {
  //        console.log('🔑 All allowed permissions:', allowed);
  // console.log('🎯 Opponent check:', {
  //   'Opponent_Strength': allowed.includes("Opponent_Strength"),
  //   'Opponent_Strength_Permission': allowed.includes("Opponent_Strength_Permission"),
  //   'opponent_strength': allowed.includes("opponent_strength"),
  // });
      setHasTMManagerAccess(allowed.includes("TM_Manager"));
      setHasWatchManagerAccess(allowed.includes("Watch_Manager"));
      setHasPremiumFeaturesAccess(allowed.includes("Premium_Features"));
      setHasReportsAccess(allowed.includes("Reports"));
      setHasApplyTrademarkAccess(allowed.includes("Apply_trademark"));
      setHasManageCustomerAccess(allowed.includes("Manage_Customer"));
      setWatchPermissions({
        Application_Level: allowed.includes("Application_Level"),
        Journal_Level: allowed.includes("Journal_Level"),
        Examination_Level: allowed.includes("Examination_Level"),
        Application_Status: allowed.includes("Application_Status"),
        Attorney_Status: allowed.includes("Attorney_Status"),
        Status_Change_Tracker: allowed.includes("Status_Change_Tracker"),
      });
      setPremiumFeaturesPermissions({
        Astro_Mark: allowed.includes("Astro_Mark"),
        Proprietor_Status: allowed.includes("Proprietor_Status"),
        Single_Click_Report: allowed.includes("Single_Click_Report"),
        Focus_Word: allowed.includes("Focus_Word"),
        Opponent_Strength_Permission: allowed.includes("Opponent_Strength_Permission"),  // ✅ ADD THIS

      });
      setReportsPermissions({
        Search_Report: allowed.includes("Search_Report"),
      });
      setCanDraftLegal(allowed.includes("can_draft_legal"));
      setCanManageCases(allowed.includes("can_manage_cases"));
      setCanUseBrandGenerator(allowed.includes("Brand_Generator_Permission"));
      setCanUseWhatsAppSupport(allowed.includes("WhatsApp_Support_Permission"));
      setCanUseWhatsAppSearch(allowed.includes("WhatsApp_Search_Permission"));
      setCanClientBillingPermission(allowed.includes("Client_Billing_Permission"));
      setCanTMAvailabilityPredictor(allowed.includes("TM_Availability_Predictor"));
      setCanTaskManagement(allowed.includes("Task_Management_Permission"));
      setHasDesignSearchAccess(allowed.includes("Design_Search_Permission"));
      setHasPatentSearchAccess(allowed.includes("Patent_Search_Permission"));
      setHasInternationalSearchAccess(allowed.includes("International_Search_Permission"));
      setCanClientEmailSetting(allowed.includes("Client_Email_Setting"));
      setCanClientEmailTemplate(  allowed.includes("Client_Email_Template"));
    };

    const resetPermissions = () => {
 
      if (!mounted) return;
      setHasTMManagerAccess(false);
      setHasWatchManagerAccess(false);
      setHasPremiumFeaturesAccess(false);
      setHasReportsAccess(false);
      setHasApplyTrademarkAccess(false);
      setHasManageCustomerAccess(false);
      setCanDraftLegal(false);
      setCanManageCases(false);
      setCanUseWhatsAppSupport(false);
      setCanUseWhatsAppSearch(false);
      setCanClientBillingPermission(false);
      setCanTMAvailabilityPredictor(false);
      setHasDesignSearchAccess(false);
      setHasPatentSearchAccess(false);
      setHasInternationalSearchAccess(false);
      setCanClientEmailSetting(false);
setCanClientEmailTemplate(false);
    };

    if (userType === USER_TYPES.CLIENT) {
      loadClientLicense();
    } else if (userType === USER_TYPES.CLIENT_USER) {
      loadClientChildLicense();
    } else {
      resetPermissions();
      setLicenseLoading(false);
    }

    return () => { mounted = false; };
  }, [userType]);

  const [expandedMenus, setExpandedMenus] = useState({
    tmManager: false,
    yourTrademark: false,
    opponentTrademark: false,
    watchManager: false,
    premiumFeatures: false,
    reports: false,
    utility: false,
  });

  const toggleMenu = (menuKey, forceOpen) => {
    setExpandedMenus((prev) => {
      if (menuKey === "tmManager" && !hasTMManagerAccess) return prev;
      if (menuKey === "watchManager" && !hasWatchManagerAccess) return prev;
      if (menuKey === "premiumFeatures" && !hasPremiumFeaturesAccess) return prev;
      if (menuKey === "reports" && !hasReportsAccess) return prev;
      return {
        ...prev,
        [menuKey]: typeof forceOpen === "boolean" ? forceOpen : !prev[menuKey],
      };
    });
  };

  const isActiveRoute = (path, { exact = false } = {}) => {
    if (!path) return false;
    const match = matchPath({ path, end: exact }, location.pathname);
    return Boolean(match);
  };

  const legalMenuItems = [];
  if (canDraftLegal) {
    legalMenuItems.push({ id: "legalDrafting", href: ROUTES.LEGAL_DRAFTING, icon: Scale, name: "Legal Drafting" });
  }
  if (canManageCases) {
    legalMenuItems.push({ id: "caseLibrary", href: ROUTES.LEGAL_DRAFTING_CASES, icon: BookOpen, name: "Case Library" });
  }

  const navigationStructure = [
    ...(hasTMManagerAccess ? [{ id: "Portfolio", name: "Portfolio", href: "/tm/portfolio", icon: BarChart3, type: "link", badgeColor: "bg-gradient-to-r from-blue-500 to-blue-600" }] : []),
    ...(hasApplyTrademarkAccess ? [{ id: "applyingTrademark", name: t("Apply Trademark"), href: ROUTES.Applying_Trademark, icon: FilePlus2, type: "link" }] : []),
    { id: "search", name: t("nav.search"), href: ROUTES.SEARCH, icon: Search, type: "link" },
{id: "internationalSearch",name: "International TM Search",href: ROUTES.INTERNATIONAL_SEARCH,icon: Globe,type: "link",disabled: !hasInternationalSearchAccess, badge: "Beta", badgeColor: "bg-gradient-to-r from-violet-500 to-purple-600"},    { id: "designSearch", name: "Design Search", href: ROUTES.DESIGN_SEARCH, icon: Palette, type: "link", disabled: !hasDesignSearchAccess},
    {id: "patentSearch",name: "Patent Search",href: ROUTES.PATENT_SEARCH,icon: ScrollText,type: "link", disabled: !hasPatentSearchAccess},
    { id: "searchHistory", name: t("search.searchHistory"), href: ROUTES.SEARCH_HISTORY, icon: History, type: "link" },
    {
      id: "tmManager", name: "TM Manager", icon: Layers, type: "parent", expanded: expandedMenus.tmManager,
      children: [
        {
          id: "yourTrademark", name: "Your Trademark", icon: BadgeCheck, type: "parent", expanded: expandedMenus.yourTrademark,
          children: [
            { id: "tmDashboard", name: "Calendar", href: "/tm/your-trademark/dashboard", icon: Calendar, type: "link" },
            { id: "deadlineManagement", name: "Deadline Management", href: "/tm/your-trademark/deadlines", icon: AlarmClock, type: "link" },
            { id: "allTrademark", name: "Your Trademark", href: "/tm/your-trademark/all", icon: List, type: "link" },
          ],
        },
        {
          id: "opponentTrademark", name: "Opponent Trademark", icon: UserCheck, type: "parent", expanded: expandedMenus.opponentTrademark,
          children: [
            { id: "opponentTmDashboard", name: "Calendar", href: "/tm/opponent/dashboard", icon: Calendar, type: "link" },
            { id: "opponentDeadlineManagement", name: "Deadline Management", href: "/tm/opponent/deadlines", icon: AlarmClock, type: "link" },
            { id: "opponentAllTrademark", name: "Opponent Trademark", href: "/tm/opponent/all", icon: List, type: "link" },
          ],
        },
        { id: "manageTrademark", name: "Manage Trademark", href: "/tm/manage", icon: Settings, type: "link" },
        { id: "PortfolioDeadlineManagement", name: "Portfolio Deadline", href: "/tm/portfolio-deadline-management", icon: CalendarClock, type: "link" },
        { id: "PortfolioTrademarks", name: "Portfolio Trademarks", href: "/tm/portfolio-trademarks/all", icon: FolderKanban, type: "link" },
      ],
    },
    {
      id: "watchManager", name: "Watch Manager", icon: Eye, type: "parent", expanded: expandedMenus.watchManager, disabled: !hasWatchManagerAccess,
      children: [
        ...(!preferences?.simplified_view ? [
          { id: "applicationLevel", name: "Application Level", icon: FileText, type: "link", href: ROUTES.Application_Level, permissionKey: "Application_Level" },
          { id: "journalLevel", name: "Journal Level", icon: UploadCloud, type: "link", href: ROUTES.Journal_Level, permissionKey: "Journal_Level" },
          { id: "examinationLevel", name: "Examination Level", icon: ClipboardCheck, type: "link", href: ROUTES.Examination_Report, permissionKey: "Examination_Level" },
        ] : []),
        { id: "applicationStatus", name: "External Application Status", icon: ActivitySquare, type: "link", href: ROUTES.Application_Status, permissionKey: "Application_Status" },
        { id: "attorneyStatus", name: "Attorney Status", icon: Scale, type: "link", href: ROUTES.Attorney_Status, permissionKey: "Attorney_Status" },
        { id: "changeTracker", name: "Change Tracker", icon: GrDocument, type: "link", href: ROUTES.Change_Tracker, permissionKey: "Status_Change_Tracker" },
      ],
    },
    {
      id: "premiumFeatures", name: "Premium Features", icon: Zap, type: "parent", expanded: expandedMenus.premiumFeatures, disabled: !hasPremiumFeaturesAccess, isPremium: true,
      children: [
        { id: "astroMark", name: "Astro Mark", icon: Sparkles, type: "link", badge: "New", href: ROUTES.Astro_Mark, premiumFeaturesPermissionKey: "Astro_Mark" },
        ...(!preferences?.simplified_view ? [{ id: "focusWord", name: "Focus Word", icon: BsWordpress, type: "link", href: ROUTES.Focus_Word, premiumFeaturesPermissionKey: "Focus_Word" }] : []),
        { id: "proprietorStatus", name: "Proprietor Tracker", icon: UserCheck, type: "link", href: ROUTES.Proprietor_Status, premiumFeaturesPermissionKey: "Proprietor_Status" },
        { id: "singleClickReport", name: "Single Click Cited Reports", icon: BiSolidReport, type: "link", href: ROUTES.Single_Click_Report, premiumFeaturesPermissionKey: "Single_Click_Report" },
        { id: "opponentStrength", name: "Opponent Strength", icon: Target, type: "link", href: ROUTES.OPPONENT_STRENGTH, premiumFeaturesPermissionKey: "Opponent_Strength_Permission" },

      ],
    },
    {
      id: "reports", name: "Reports & Analytics", icon: LineChart, type: "parent", expanded: expandedMenus.reports, disabled: !hasReportsAccess,
      children: [
        { id: "notificationAnalytics", name: "Notification Analytics", icon: Bell, type: "link", href: ROUTES.Notification_Analytics },
        { id: "astroMarkReport", name: "AstroMark Report", icon: Star, type: "link", href: ROUTES.Astro_Mark_Report, premiumFeaturesPermissionKey: "Astro_Mark" },
        { id: "searchReport", name: "Search Report", icon: FileSearch, type: "link", href: ROUTES.Search_Report, reportsPermissionKey: "Search_Report" },
        { id: "dashboard", name: t("Usage Statistics"), href: ROUTES.SEARCH_STATISTICS, icon: Database, type: "link" },
      ],
    },
    {
      id: "utility", name: "Utility", icon: GiFocusedLightning, type: "parent", expanded: expandedMenus.utility,
      children: [{ id: "imageConverter", name: "Image Converter", icon: LuImageDown, type: "link", href: ROUTES.Image_Converter }],
    },
    ...(hasManageCustomerAccess ? [{ id: "manageCustomer", name: t("Manage Customer"), href: ROUTES.Manage_Customer, icon: FaUserCog, type: "link" }] : []),
  ];

  if (userType === USER_TYPES.CLIENT && maxUsersAllowed && maxUsersAllowed > 0) {
    // Admin Panel section handles this
  }

  const settingsItems = [
    { id: "manageTags", name: t("Manage Tags"), href: ROUTES.MANAGE_TAGS, icon: Tag },
    { id: "profile", name: t("nav.profile"), href: ROUTES.PROFILE, icon: User },
    ...(userType === USER_TYPES.CLIENT ? [
      { id: "preferences", name: t("nav.preferences"), href: ROUTES.PREFERENCES, icon: Sliders },
        { id: "license", name: t("nav.license"), href: ROUTES.LICENSE, icon: Key },
      ...(canUseWhatsAppSearch ? [{ id: "whatsappSetup", name: "WhatsApp Search", href: ROUTES.WHATSAPP_SETUP, icon: MessageSquare, badge: "Beta", badgeColor: "bg-gradient-to-r from-green-500 to-emerald-600" }] : []),
  ...(canClientEmailSetting ? [{
    id: "emailSettings",
    name: "Email Settings",
    href: ROUTES.EMAIL_SETTINGS,
    icon: Mail,
  }] : []),

  ...(canClientEmailTemplate ? [{
    id: "clientEmailTemplate",
    name: "Email Templates",
    href: ROUTES.CLIENT_EMAIL_TEMPLATE,
    icon: FileText,
  }] : []),
    ] : []),
  ];

  const renderMenuItem = (item, level = 0) => {
    if (item.hidden) return null;
    const Icon = item.icon;
    const isActive = (() => {
      if (item.href) return isActiveRoute(item.href, { exact: true });
      if (item.children && item.children.length) return item.children.some(child => child.href && isActiveRoute(child.href, { exact: false }));
      return false;
    })();
    const isExpanded = item.expanded;

    if (item.type === "parent") {
      const isParentDisabled =
        (item.id === "tmManager" && !hasTMManagerAccess && !licenseLoading) ||
        (item.id === "watchManager" && !hasWatchManagerAccess && !licenseLoading) ||
        (item.id === "premiumFeatures" && !hasPremiumFeaturesAccess && !licenseLoading) ||
        (item.id === "reports" && !hasReportsAccess && !licenseLoading);

      return (
        <div key={item.id} className="relative">
          <button
            type="button"
            disabled={isParentDisabled}
            onClick={() => { if (isParentDisabled) return; toggleMenu(item.id); }}
            onMouseEnter={() => setHoveredItem(item.id)}
            onMouseLeave={() => setHoveredItem(null)}
            className={`group relative flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ease-out ${isParentDisabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"} ${isExpanded ? "bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300" : isActive ? "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white" : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"}`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="relative flex items-center justify-center flex-shrink-0 w-5 h-5">
                <Icon className={`w-5 h-5 transition-all duration-200 ${isExpanded ? "text-blue-600 dark:text-blue-400" : isParentDisabled ? "text-gray-400 dark:text-gray-600" : "text-gray-600 dark:text-gray-400"}`} />
                {item.isPremium && (
                  <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-sm">
                    <Star className="w-2 h-2 text-white fill-white" />
                  </div>
                )}
              </div>
              <span className={`transition-colors duration-200 truncate ${isExpanded ? "text-blue-700 dark:text-blue-300 font-semibold" : isParentDisabled ? "text-gray-400 dark:text-gray-600" : "text-gray-700 dark:text-gray-300"}`}>
                {item.name}
              </span>
            </div>
            <ChevronDown className={`h-4 w-4 flex-shrink-0 transition-all duration-200 ease-out ml-2 ${isExpanded ? "rotate-180" : "rotate-0"} ${isExpanded ? "text-blue-600 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"}`} />
          </button>
          <div className={`overflow-hidden transition-all duration-200 ease-out ${!isParentDisabled && isExpanded ? "max-h-[600px] opacity-100 mt-0.5" : "max-h-0 opacity-0"}`}>
            <div className="ml-3 pl-4 border-l-2 border-gray-200 dark:border-gray-700 space-y-0.5 py-1">
              {item.children?.filter(child => { if (child.permissionKey === "Attorney_Status") return watchPermissions.Attorney_Status; return true; }).map(child => renderMenuItem(child, level + 1))}
            </div>
          </div>
        </div>
      );
    }

    const inDisabledTMManager = level >= 1 && expandedMenus.tmManager && !hasTMManagerAccess && !licenseLoading;
    const inDisabledWatchManager = level >= 1 && expandedMenus.watchManager && !hasWatchManagerAccess && !licenseLoading;
    const inDisabledPremiumFeatures = level >= 1 && expandedMenus.premiumFeatures && !hasPremiumFeaturesAccess && !licenseLoading;
    const inDisabledReports = level >= 1 && expandedMenus.reports && !hasReportsAccess && !licenseLoading;
    const isWatchSubMenu = level >= 1 && expandedMenus.watchManager && item.permissionKey;
    const isPremiumFeaturesSubMenu = level >= 1 && expandedMenus.premiumFeatures && item.premiumFeaturesPermissionKey;
    const hasWatchSubPermission = isWatchSubMenu ? watchPermissions[item.permissionKey] : true;
    const hasPremiumFeaturesPermission = isPremiumFeaturesSubMenu ? premiumFeaturesPermissions[item.premiumFeaturesPermissionKey] : true;
    const isReportsMenu = level >= 1 && expandedMenus.reports && (item.reportsPermissionKey || item.premiumFeaturesPermissionKey);
    const hasReportsSubPermission = item.reportsPermissionKey ? reportsPermissions[item.reportsPermissionKey] : item.premiumFeaturesPermissionKey   ? premiumFeaturesPermissions[item.premiumFeaturesPermissionKey]   : true;
    const isDisabled = item.disabled || inDisabledTMManager || inDisabledWatchManager || inDisabledPremiumFeatures || inDisabledReports || !hasWatchSubPermission || !hasPremiumFeaturesPermission || !hasReportsSubPermission;

    return (
      <Link
        key={item.id}
        to={isDisabled ? "#" : item.href}
        onClick={e => { if (isDisabled) { e.preventDefault(); return; } onClose?.(); }}
        className={`group relative flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ease-out ${isDisabled ? "opacity-30 cursor-not-allowed" : isActive ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md" : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"}`}
      >
        {isActive && !isDisabled && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-white/70 rounded-r-full" />}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="flex items-center justify-center flex-shrink-0 w-5 h-5">
            <Icon className={`w-5 h-5 transition-colors duration-200 ${isActive && !isDisabled ? "text-white" : isDisabled ? "text-gray-400 dark:text-gray-600" : "text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white"}`} />
          </div>
          <span className="truncate">{item.name}</span>
        </div>
        {item.badge && !isDisabled && (
          <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full ${item.badgeColor || "bg-blue-500"} text-white flex-shrink-0 ml-2`}>{item.badge}</span>
        )}
      </Link>
    );
  };

  // Reusable section divider
  const SectionDivider = ({ label }) => (
    <div className="relative py-4">
      <div className="absolute inset-0 flex items-center px-3">
        <div className="w-full border-t border-gray-200 dark:border-gray-800" />
      </div>
      <div className="relative flex justify-center">
        <span className="px-3 text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider bg-white dark:bg-gray-900">{label}</span>
      </div>
    </div>
  );

  // Reusable nav link (same style as Legal Drafting items)
  const NavLink = ({ href, icon: Icon, name, exact = false }) => {
    const isActive = isActiveRoute(href, { exact });
    return (
      <Link
        to={href}
        onClick={() => onClose && onClose()}
        className={`group relative flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ease-out ${isActive ? 'bg-gradient-to-r from-violet-600 to-blue-700 text-white shadow-md' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
      >
        {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-white/70 rounded-r-full" />}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-500 dark:text-gray-400'}`} />
          <span className="truncate">{name}</span>
        </div>
      </Link>
    );
  };

  return (
    <>
      {isMobile && isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 transition-opacity duration-300" onClick={onClose} />
      )}

      <aside className={`fixed inset-y-0 left-0 z-40 w-72 flex flex-col bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-r border-gray-200/80 dark:border-gray-800/80 shadow-2xl shadow-gray-900/10 dark:shadow-black/30 transform transition-all duration-300 ease-out ${isMobile ? isOpen ? "translate-x-0" : "-translate-x-full" : "translate-x-0"}`}>

        {/* Header */}
        <div className="flex-shrink-0 h-16 flex items-center justify-between px-4 border-b border-gray-200/80 dark:border-gray-800/80">
          <div className="flex items-center gap-3">
            <div
              className="relative w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg overflow-hidden group cursor-pointer"
              style={{ background: `linear-gradient(135deg, ${brandColors?.primary || '#3B82F6'} 0%, ${brandColors?.accent || '#1D4ED8'} 100%)` }}
            >
              <span className="relative z-10">{companyName ? companyName.charAt(0) : config.appNameShort?.charAt(0) || "T"}</span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-bold text-gray-900 dark:text-white leading-tight">{companyName || config.appNameShort || "Trademark"}</span>
              <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider">Trademark Portal</span>
            </div>
          </div>
          {isMobile && (
            <button type="button" className="p-2 rounded-xl text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200" onClick={onClose}>
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 scrollbar-track-transparent">

          {/* Main nav */}
          <div className="space-y-0.5">
            {navigationStructure.map(item => renderMenuItem(item))}
          </div>

          {/* Tasks */}
          {canTaskManagement && (
            <>
              <SectionDivider label="Tasks" />
              <div className="space-y-0.5">
                <NavLink href={ROUTES.TASKS} icon={Briefcase} name="Task Board" exact />
                <NavLink href={ROUTES.TASK_REMINDERS} icon={Bell} name="Deadline Reminders" exact />
              </div>
            </>
          )}

          {/* Legal Drafting */}
          {legalMenuItems.length > 0 && (
            <>
              <SectionDivider label="Legal Drafting" />
              <div className="space-y-0.5">
                {legalMenuItems.map(item => (
                  <NavLink key={item.id} href={item.href} icon={item.icon} name={item.name} exact={item.id === 'legalDrafting'} />
                ))}
              </div>
            </>
          )}

          {/* Brand Tools */}
          <>
            {(canUseBrandGenerator || canTMAvailabilityPredictor) && (
              <>
                <SectionDivider label="Brand Tools" />
                <div className="space-y-0.5">

                  {canUseBrandGenerator && (
                    <NavLink
                      href={ROUTES.BRAND_GENERATOR}
                      icon={Sparkles}
                      name="Brand Generator"
                      exact
                    />
                  )}

                  {canTMAvailabilityPredictor && (
                    <NavLink
                      href={ROUTES.TRADEMARK_PREDICTOR}
                      icon={FileSearch}
                      name="TM Availability Predictor"
                      exact
                    />
                  )}

                </div>
              </>
            )}
          </>
          {/* ── Client Billing — CLIENT users only ── */}

          {userType === USER_TYPES.CLIENT && canClientBillingPermission && (
            <>
              <SectionDivider label="Billing" />
              <div className="space-y-0.5">
                <NavLink href={ROUTES.BILLING} icon={ReceiptText} name="Client Billing" />
              </div>
            </>
          )}

          {/* Admin Panel */}
        {hookTeamAccess && (userType === USER_TYPES.CLIENT || perms?.manage_users) && (
  <>
    <SectionDivider label="Admin Panel" />
    <div className="space-y-0.5">
      {[
        // ── CLIENT OWNER ONLY items ──────────────────────────────
        // Dashboard
        ...(userType === USER_TYPES.CLIENT
          ? [{ id: 'adminPanel', href: ROUTES.ADMIN_PANEL, icon: Crown, name: 'Dashboard' }]
          : []),

        // Team Overview — visible to client owner + child users with manage_users
        { id: 'adminPanelUsers', href: ROUTES.ADMIN_PANEL_USERS, icon: Users, name: 'Team Overview' },

        // Roles & Permissions — client owner only
        ...(userType === USER_TYPES.CLIENT
          ? [{ id: 'adminPanelRoles', href: ROUTES.ADMIN_PANEL_ROLES, icon: Shield, name: 'Roles & Permissions' }]
          : []),

        // Team Groups — client owner only
        ...(userType === USER_TYPES.CLIENT
          ? [{ id: 'adminPanelTeams', href: ROUTES.ADMIN_PANEL_TEAMS, icon: Users, name: 'Team Groups' }]
          : []),

        // Org Hierarchy — client owner only
        ...(userType === USER_TYPES.CLIENT
          ? [{ id: 'adminPanelHierarchy', href: ROUTES.ADMIN_PANEL_HIERARCHY || '/admin-panel/hierarchy', icon: Users, name: 'Org Hierarchy' }]
          : []),

        // Add / Edit Users — visible to client owner + child users with manage_users
        { id: 'usersLegacy', href: ROUTES.USERS, icon: UserCheck, name: 'Add / Edit Users' },

      ].map(item => {
        const Icon = item.icon;
        const isActive = isActiveRoute(item.href, { exact: item.id === 'adminPanel' });
        return (
          <Link
            key={item.id}
            to={item.href}
            onClick={() => onClose && onClose()}
            className={`group relative flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ease-out ${
              isActive
                ? 'bg-gradient-to-r from-violet-600 to-blue-700 text-white shadow-md'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            {isActive && (
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-white/70 rounded-r-full" />
            )}
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="flex items-center justify-center flex-shrink-0 w-5 h-5">
                <Icon className={`w-5 h-5 transition-colors duration-200 ${
                  isActive
                    ? 'text-white'
                    : 'text-violet-500 dark:text-violet-400 group-hover:text-violet-700 dark:group-hover:text-violet-300'
                }`} />
              </div>
              <span className="truncate">{item.name}</span>
            </div>
          </Link>
        );
      })}
    </div>
  </>
)}

          {/* Help & Support */}
          <>
          
            <SectionDivider label="Help &amp; Support" />
          
            <div className="space-y-0.5">
              <NavLink href="/support" icon={LifeBuoy} name="Raise a Ticket" exact />
              {canUseWhatsAppSupport && (
              <a
                href="https://wa.me/919289161375"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onClose && onClose()}
                className="group flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ease-out text-gray-700 dark:text-gray-300 hover:bg-green-50 dark:hover:bg-green-900/20 hover:text-green-700 dark:hover:text-green-400"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <svg className="h-4 w-4 text-green-500 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span className="truncate">WhatsApp Support</span>
                </div>
                <span className="text-[9px] text-green-600 font-bold bg-green-100 dark:bg-green-900/30 px-1.5 py-0.5 rounded-full flex-shrink-0">Chat</span>
              </a>
                )}
            </div>
          </>

          {/* Settings */}
          <SectionDivider label={t("nav.settings")} />
          <div className="space-y-0.5">
            {settingsItems.map(item => {
              const Icon = item.icon;
              const isActive = isActiveRoute(item.href);
              return (
                <Link
                  key={item.id}
                  to={item.href}
                  onClick={onClose}
                  className={`group flex items-center gap-3 pl-3 pr-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ease-out ${isActive ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md" : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"}`}
                >
                  {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-white/70 rounded-r-full" />}
                  <div className="flex items-center justify-center flex-shrink-0 w-4 h-4">
                    <Icon className={`w-4 h-4 transition-colors duration-200 ${isActive ? "text-white" : "text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white"}`} />
                  </div>
                  <span className="flex-1">{item.name}</span>
                  {item.badge && (
                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full ${item.badgeColor || "bg-blue-500"} text-white flex-shrink-0`}>{item.badge}</span>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-gray-200/80 dark:border-gray-800/80 p-3 space-y-3">
          <div className="flex items-center justify-between px-3">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[10px] text-gray-500 dark:text-gray-400">{config.appName || "Trademark Portal"}</span>
            </div>
            <span className="text-[10px] font-mono text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">v{config.version || "1.0.0"}</span>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;